import {
  AmountInput,
  Button,
  FormField,
  type HighlightIconVariant,
  type IconName,
  Modal,
  TextArea,
} from '@dev-spendesk/grapes';
import { type FormikErrors, useFormik } from 'formik';
import React from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { getCurrencyOptionByKey } from 'src/core/utils/money';

export type FormValues = {
  amount: number | null;
  description: string;
};

export type Props = {
  mode: 'admin' | 'requester';
  onCancel: () => void;
  onSubmitAdmin: (amount: number) => Promise<void> | void;
  onSubmitRequester: (
    amount: number,
    description: string,
  ) => Promise<void> | void;
  validate: (values: FormValues) => Promise<FormikErrors<FormValues>>;
  title: string;
  iconName?: IconName;
  iconVariant?: HighlightIconVariant;
  subtitle?: string;
  amountLabel?: string;
  amountPlaceholder?: string;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
};

export const TopUpModalLayout = ({
  mode,
  onCancel,
  onSubmitAdmin,
  onSubmitRequester,
  validate,
  title,
  iconName = 'card-arrow-up',
  iconVariant,
  subtitle,
  amountLabel,
  amountPlaceholder,
  descriptionLabel,
  descriptionPlaceholder,
  submitButtonText,
  cancelButtonText,
}: Props) => {
  const { t } = useTranslation('global');
  const company = useCompany();

  const formikProps = useFormik<FormValues>({
    initialValues: {
      amount: null,
      description: '',
    },
    validateOnChange: false,
    validate,
    onSubmit: async (values) => {
      if (!values.amount) {
        return;
      }

      if (mode === 'admin') {
        await onSubmitAdmin(values.amount);
      }
      if (mode === 'requester') {
        await onSubmitRequester(values.amount, values.description);
      }
    },
  });

  return (
    <Modal
      isOpen
      title={title}
      subtitle={subtitle}
      iconName={iconName}
      iconVariant={iconVariant}
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={cancelButtonText ?? t('misc.cancel')}
          onClick={onCancel}
          isDisabled={formikProps.isSubmitting}
        />,
        <Button
          key="submit"
          text={submitButtonText ?? t('cards.actions.topUpCardLimit')}
          form="card-load"
          type="submit"
          isLoading={formikProps.isSubmitting}
        />,
      ]}
    >
      <form id="card-load" onSubmit={formikProps.handleSubmit}>
        <FormField
          label={amountLabel ?? t('forms.topupCard.labelLimit')}
          alertMessage={formikProps.errors.amount}
        >
          <AmountInput
            placeholder={amountPlaceholder ?? t('forms.topupCard.placeholder')}
            value={formikProps.values.amount}
            fit="parent"
            currency={getCurrencyOptionByKey(company.currency)}
            onChange={(_, newValue) => {
              formikProps.setFieldValue('amount', newValue);
            }}
          />
        </FormField>
        {mode === 'requester' && (
          <FormField
            className="mt-24"
            label={descriptionLabel ?? t('forms.topupCard.descriptionLabel')}
            alertMessage={formikProps.errors.description}
          >
            <TextArea
              name="description"
              placeholder={
                descriptionPlaceholder ??
                t('forms.topupCard.descriptionPlaceholder')
              }
              maxLength={255}
              value={formikProps.values.description}
              onChange={formikProps.handleChange}
            />
          </FormField>
        )}
      </form>
    </Modal>
  );
};
